/**
 * Utilidades de tiempo transcurrido — estándar de duraciones del aplicativo.
 *
 * Toda duración que se muestre al usuario se descompone en las unidades
 * grandes que apliquen (años, meses, días, horas, minutos, segundos) en vez
 * de acumular una sola unidad ("5.000 horas" está prohibido). Aproximaciones
 * fijas: 1 año = 365 días, 1 mes = 30 días — suficiente para texto de UI.
 */

const MINUTE_S = 60;
const HOUR_S = 60 * MINUTE_S;
const DAY_S = 24 * HOUR_S;
const MONTH_S = 30 * DAY_S;
const YEAR_S = 365 * DAY_S;

/**
 * Descompone una duración en segundos en sus componentes de calendario.
 *
 * @param {number} totalSeconds — duración no negativa en segundos
 * @returns {{years:number, months:number, days:number, hours:number, minutes:number, seconds:number}}
 */
export function decomposeDuration(totalSeconds) {
  let rest = Math.max(0, Math.floor(totalSeconds));
  const years = Math.floor(rest / YEAR_S);
  rest -= years * YEAR_S;
  const months = Math.floor(rest / MONTH_S);
  rest -= months * MONTH_S;
  const days = Math.floor(rest / DAY_S);
  rest -= days * DAY_S;
  const hours = Math.floor(rest / HOUR_S);
  rest -= hours * HOUR_S;
  const minutes = Math.floor(rest / MINUTE_S);
  const seconds = rest - minutes * MINUTE_S;
  return { years, months, days, hours, minutes, seconds };
}

const LONG_UNITS = [
  ["years", "año", "años"],
  ["months", "mes", "meses"],
  ["days", "día", "días"],
  ["hours", "hora", "horas"],
  ["minutes", "minuto", "minutos"],
];

/**
 * getRelativeTime — convierte un timestamp a texto de tiempo transcurrido.
 *
 * Compara el timestamp con la hora local actual del cliente.
 * El backend envía timestamps UTC; new Date() los convierte automáticamente
 * a la hora local, por lo que no se requiere ajuste manual de timezone.
 *
 * Formato de salida (todas las unidades con valor, de mayor a menor):
 *   futuro → "un momento" (reloj del cliente atrasado; ver abajo)
 *   < 60 s → "10 segundos" / "1 segundo"
 *   ≥ 60 s → "5 minutos" / "3 horas 20 minutos" / "1 día 5 horas 12 minutos"
 *            / "1 año 3 meses 2 días 5 horas"
 * Los segundos solo se muestran bajo el minuto — a escalas mayores son ruido
 * y con el tick compartido de 15 s parecerían saltar de a 15.
 *
 * `maxUnits` recorta a las N unidades MÁS GRANDES con valor ("5 días 8 horas"
 * en vez de "5 días 8 horas 24 minutos"). Sigue siendo descomposición —lo que
 * está prohibido es acumular en una sola unidad, no redondear la cola—, y es
 * para los lugares donde el texto compite por el ancho: la regleta de cifras
 * de una ficha, donde antes el CSS lo cortaba a la mitad de una palabra. La
 * marca de tiempo exacta vive igual en el cuerpo de la ficha.
 *
 * @param {string|number|Date} timestamp — ISO string, epoch ms, o Date
 * @param {{maxUnits?:number}} [options]
 * @returns {string}
 */
export function getRelativeTime(timestamp, options = {}) {
  const { maxUnits } = options;
  const diffMs = Date.now() - new Date(timestamp).getTime();

  // Marca en el futuro = reloj del cliente atrasado respecto del servidor. Se
  // mide en valor absoluto más abajo, así que sin este tope un evento recién
  // ocurrido se leía «hace 3 minutos»: fresco disfrazado de viejo. Donde más
  // duele es en los comandos —`CommandStatus` en la ficha del vehículo y el
  // chat de Comandos muestran la edad del MISMO comando junto a los botones de
  // relé—, y leer un envío fresco como viejo invita a repetir un corte de
  // combustible. Va acá y no en un call site: con la guarda en uno solo, las
  // dos pantallas se contradecían sobre el mismo dato.
  if (diffMs < 0) return "un momento";

  const totalSeconds = Math.floor(diffMs / 1000);

  if (totalSeconds < 60) {
    return totalSeconds === 1 ? "1 segundo" : `${totalSeconds} segundos`;
  }

  const parts = decomposeDuration(totalSeconds);
  const conValor = LONG_UNITS.filter(([key]) => parts[key] > 0);
  const mostradas =
    typeof maxUnits === "number" && maxUnits > 0
      ? conValor.slice(0, maxUnits)
      : conValor;

  return mostradas
    .map(([key, singular, plural]) =>
      parts[key] === 1 ? `1 ${singular}` : `${parts[key]} ${plural}`,
    )
    .join(" ");
}
